/**
 * PermitOperationalStrip — Phase 2 cross-domain visibility primitives.
 *
 * Read-only display over `resolvePermitOperationalSignals()`. Two shapes:
 *  - <PermitOperationalStrip job /> — full strip for Job Detail.
 *  - <PermitOperationalChip job variant="readiness|scheduling|closeout" />
 *    — compact row chip for list surfaces.
 *
 * NO automation. NO writers. NO action queue. Pure display.
 */
import type { Job } from "@/data/types";
import {
  resolvePermitOperationalSignals,
  type PermitOperationalSeverity,
  type PermitOperationalSignals,
} from "@/data/permitting/permitOperationalSignals";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, AlertTriangle, XCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

function severityClass(s: PermitOperationalSeverity): string {
  if (s === "blocker") return "bg-destructive/10 text-destructive ring-1 ring-destructive/30 border-destructive/30";
  if (s === "warn") return "bg-warning/10 text-warning ring-1 ring-warning/30 border-warning/30";
  if (s === "info") return "bg-success/10 text-success ring-1 ring-success/30 border-success/30";
  return "bg-muted/40 text-muted-foreground ring-1 ring-border/60 border-border/60";
}

function severityIcon(s: PermitOperationalSeverity) {
  if (s === "blocker") return <XCircle className="h-3.5 w-3.5" />;
  if (s === "warn") return <AlertTriangle className="h-3.5 w-3.5" />;
  if (s === "info") return <ShieldCheck className="h-3.5 w-3.5" />;
  return <Info className="h-3.5 w-3.5" />;
}

export function PermitOperationalStrip({ job, className }: { job: Job; className?: string }) {
  const sig = resolvePermitOperationalSignals(job);
  if (!job.permitRequired) return null;

  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2 flex items-start gap-3",
        severityClass(sig.operationalSeverity),
        className,
      )}
      data-testid="permit-operational-strip"
    >
      <span className="mt-0.5 shrink-0">{severityIcon(sig.operationalSeverity)}</span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight">{sig.summaryLabel}</p>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-foreground/80">
          <span>Stage: <span className="font-medium">{sig.permitStageLabel}</span></span>
          {sig.inspectionLabel && <span>· {sig.inspectionLabel}</span>}
          {sig.revisionLabel && <span>· {sig.revisionLabel}</span>}
          {sig.closeoutSubLabel && <span>· {sig.closeoutSubLabel}</span>}
          <span>
            · Municipality: <span className="font-medium">{sig.municipalityProfileLabel}</span>
            {sig.usesGenericProfile && <span className="text-muted-foreground"> (generic profile)</span>}
          </span>
        </div>
      </div>
    </div>
  );
}

export type PermitChipVariant = "readiness" | "scheduling" | "closeout" | "summary";

interface ChipProps {
  job: Job;
  variant?: PermitChipVariant;
  className?: string;
  /** When true, hides the chip entirely if the signal is `not_required` or `no_signal`. */
  hideWhenSilent?: boolean;
}

function chipForVariant(sig: PermitOperationalSignals, variant: PermitChipVariant) {
  if (variant === "readiness") {
    const sev: PermitOperationalSeverity =
      sig.readinessStatus === "permit_blocked" || sig.readinessStatus === "inspection_correction_required"
        ? "blocker"
        : sig.readinessStatus === "awaiting_review"
          ? "warn"
          : sig.readinessStatus === "permit_ready"
            ? "info"
            : sig.readinessStatus === "not_required"
              ? "neutral"
              : "warn";
    return { sev, label: sig.readinessLabel, silent: sig.readinessStatus === "not_required" };
  }
  if (variant === "scheduling") {
    const sev: PermitOperationalSeverity =
      sig.schedulingStatus === "permit_blocked" || sig.schedulingStatus === "failed_inspection"
        ? "blocker"
        : sig.schedulingStatus === "municipality_review"
          ? "warn"
          : sig.schedulingStatus === "permit_cleared"
            ? "info"
            : sig.schedulingStatus === "not_required"
              ? "neutral"
              : "warn";
    return { sev, label: sig.schedulingLabel, silent: sig.schedulingStatus === "not_required" };
  }
  if (variant === "closeout") {
    const sev: PermitOperationalSeverity =
      sig.closeoutStatus === "municipality_closure_confirmed"
        ? "info"
        : sig.closeoutStatus === "ready_for_closure_confirmation"
          ? "warn"
          : sig.closeoutStatus === "checklist_in_progress"
            ? "neutral"
            : "neutral";
    return {
      sev,
      label: sig.closeoutLabel,
      silent: sig.closeoutStatus === "not_required" || sig.closeoutStatus === "no_signal",
    };
  }
  return { sev: sig.operationalSeverity, label: sig.summaryLabel, silent: false };
}

export function PermitOperationalChip({
  job,
  variant = "summary",
  className,
  hideWhenSilent = false,
}: ChipProps) {
  const sig = resolvePermitOperationalSignals(job);
  const { sev, label, silent } = chipForVariant(sig, variant);
  if (hideWhenSilent && silent) return null;
  if (!job.permitRequired && hideWhenSilent) return null;
  return (
    <Badge
      variant="outline"
      className={cn("gap-1 text-[10px] font-medium", severityClass(sev), className)}
      title={label}
      data-testid={`permit-chip-${variant}`}
    >
      {severityIcon(sev)}
      <span className="truncate max-w-[180px]">{label}</span>
    </Badge>
  );
}
